import React, { useState, useEffect, useCallback } from 'react';
import { Heart, ArrowLeft, RefreshCw, ShieldCheck } from 'lucide-react';
import { API } from '../App';

/* Highlight text after the first "-" in primary color */
function TitleHighlight({ text, color }) {
  const idx = text.indexOf('-');
  if (idx === -1) return <>{text}</>;
  return <>{text.slice(0, idx)}<span style={{ color }}>{text.slice(idx)}</span></>;
}

export default function RequestAccessPage() {
  const [email, setEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaChallenge, setCaptchaChallenge] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#0092B5');
  const [loginBg, setLoginBg] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [texts, setTexts] = useState({
    logo_title: '', logo_subtitle: '',
    hero_title: '', hero_subtitle: '', hero_description: '',
    footer: '',
  });
  const [at, setAt] = useState({
    page_heading: 'Zugang anfordern',
    info_title: 'Haben Sie noch keinen Zugang zum internen Bereich?',
    info_body: '',
    info_important: '',
    submit_button: 'Zugang anfordern',
    success_message: '',
    back_link: 'Zurück zum Login',
  });

  const loadCaptcha = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/auth/captcha`);
      const d = await r.json();
      setCaptchaToken(d.token);
      setCaptchaChallenge(d.challenge);
      setCaptchaAnswer('');
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadCaptcha();
    fetch(`${API}/api/settings/design-config/public`).then(r => r.json()).then(d => {
      if (d?.title) document.title = d.title;
      if (d?.primary) setPrimaryColor(d.primary);
      if (d?.login_bg) setLoginBg(`${API}${d.login_bg}`);
      else setLoginBg('');
      if (d?.login_texts) setTexts(prev => ({ ...prev, ...d.login_texts }));
      if (d?.access_texts) setAt(prev => ({ ...prev, ...d.access_texts }));
      setConfigLoaded(true);
    }).catch(() => setConfigLoaded(true));
  }, [loadCaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!captchaAnswer.trim()) { setError('Bitte lösen Sie den Sicherheitscode.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, captcha_token: captchaToken, captcha_answer: captchaAnswer }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Fehler'); loadCaptcha(); }
      else { setSent(true); }
    } catch (err) { setError('Netzwerkfehler. Bitte versuchen Sie es erneut.'); loadCaptcha(); }
    finally { setLoading(false); }
  };

  const year = new Date().getFullYear();
  const footerText = (texts.footer || '').replace(/\{year\}/g, year);

  if (!configLoaded) return <div style={{ minHeight: '100vh', background: '#fff' }} />;

  return (
    <div data-testid="request-access-page" style={S.wrapper}>
      {/* LEFT */}
      <div style={S.left}>
        <div style={S.logo} data-testid="access-brand">
          <div style={{ ...S.logoIcon, background: primaryColor }}>
            <Heart size={18} color="#fff" fill="#fff" />
          </div>
          <div style={{ lineHeight: 1.15 }}>
            <div style={S.logoTitle}>
              <TitleHighlight text={texts.logo_title || 'BGPR Mitglieder-Portal'} color={primaryColor} />
            </div>
            <div style={S.logoSub}>{texts.logo_subtitle || 'Mitgliederportal'}</div>
          </div>
        </div>

        <div style={S.formArea}>
          <h1 style={S.heading}>{(at.page_heading || 'Zugang anfordern').toUpperCase()}</h1>
          <div style={{ ...S.headingBar, background: primaryColor }} />
          <div style={S.headingDivider} />

          {sent ? (
            <div style={{ marginTop: 32 }}>
              <div style={S.successBox} data-testid="access-success">
                {at.success_message || 'Falls diese E-Mail-Adresse im System hinterlegt ist, erhalten Sie in Kürze eine Nachricht mit einem Link zur Einrichtung Ihres Passworts. Bitte prüfen Sie auch Ihren Spam-Ordner.'}
              </div>
              <a href="/login" style={{ ...S.submitBtn, background: primaryColor, display: 'block', textDecoration: 'none', textAlign: 'center' }} data-testid="access-back-login">
                {(at.back_link || 'Zurück zum Login').toUpperCase()}
              </a>
            </div>
          ) : (
            <>
              <div style={S.infoBox} data-testid="access-info-text">
                <strong>{at.info_title || 'Haben Sie noch keinen Zugang zum internen Bereich?'}</strong><br /><br />
                {at.info_body || 'Als Mitglied sind Sie bereits im BGPR-Mitgliederportal angelegt. Geben Sie unten Ihre bei der BGPR hinterlegte E-Mail-Adresse sowie den angezeigten Sicherheitscode ein. Sie erhalten anschließend einen Link, mit dem Sie Ihr Passwort einrichten können.'}
                {at.info_important && (
                  <><br /><br /><strong>Wichtig:</strong> {at.info_important}</>
                )}
              </div>

              {error && <div style={S.error} data-testid="access-error">{error}</div>}

              <form onSubmit={handleSubmit} style={{ marginTop: 24 }}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>E-MAIL-ADRESSE</label>
                  <input style={S.input} type="email" placeholder="ihre@email.de"
                    value={email} onChange={e => setEmail(e.target.value)} required
                    data-testid="access-email-input" />
                </div>

                <div style={S.fieldGroup}>
                  <label style={{ ...S.label, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ShieldCheck size={13} /> SICHERHEITSCODE
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={S.captchaBox} data-testid="captcha-challenge">
                      {captchaChallenge || '...'}
                    </div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#6B7280' }}>=</span>
                    <input style={{ ...S.input, width: 70, textAlign: 'center', fontSize: 16, fontWeight: 600, padding: '10px 8px' }}
                      type="text" inputMode="numeric" pattern="[0-9]*" placeholder="?"
                      value={captchaAnswer} onChange={e => setCaptchaAnswer(e.target.value)}
                      data-testid="captcha-answer" required />
                    <button type="button" onClick={loadCaptcha} title="Neue Aufgabe"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4 }}
                      data-testid="captcha-refresh">
                      <RefreshCw size={16} />
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading} data-testid="access-submit-btn"
                  style={{ ...S.submitBtn, background: primaryColor, opacity: loading ? 0.7 : 1 }}>
                  {loading ? 'WIRD GESENDET...' : (at.submit_button || 'Zugang anfordern').toUpperCase()}
                </button>
              </form>

              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <a href="/login" style={S.backLink} data-testid="access-login-link">
                  <ArrowLeft size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {at.back_link || 'Zurück zum Login'}
                </a>
              </div>
            </>
          )}
        </div>

        <div style={S.footer} data-testid="access-footer">{footerText}</div>
      </div>

      {/* RIGHT: Hero Image */}
      <div style={S.right}>
        {loginBg && <div style={{ ...S.heroBg, backgroundImage: `url("${loginBg}")` }} />}
        {!loginBg && <div style={{ ...S.heroBg, background: '#5f8a8b' }} />}
        <div style={S.heroTint} />
        <div style={S.heroRadial} />
        <div style={S.heroContent}>
          <div style={S.heroIcon}>
            <Heart size={36} color="#fff" fill="#fff" />
          </div>
          <h2 style={S.heroTitle}>
            <TitleHighlight text={texts.hero_title || 'BGPR Mitglieder-Portal'} color="rgba(255,255,255,0.90)" />
          </h2>
          <div style={{ ...S.heroBar, background: primaryColor }} />
          <p style={S.heroSub}>{(texts.hero_subtitle || 'Mitgliederportal').toUpperCase()}</p>
          <p style={S.heroDesc}>{texts.hero_description || ''}</p>
        </div>
      </div>
    </div>
  );
}

const S = {
  wrapper: { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" },
  left: { flex: '0 0 50%', display: 'flex', flexDirection: 'column', padding: '40px 60px', background: '#fff', position: 'relative' },
  logo: { display: 'flex', alignItems: 'center', gap: 12 },
  logoIcon: { width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  logoTitle: { fontSize: 16, fontWeight: 800, color: '#1a2035', lineHeight: 1.2 },
  logoSub: { fontSize: 9, fontWeight: 700, letterSpacing: 2.5, color: '#9CA3AF', textTransform: 'uppercase', marginTop: 2 },
  formArea: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 440, width: '100%' },
  heading: { fontSize: 15, fontWeight: 800, letterSpacing: 3, color: '#1a2035', margin: 0 },
  headingBar: { width: 36, height: 3, borderRadius: 2, marginTop: 12 },
  headingDivider: { width: '100%', height: 1, background: '#E5E7EB', marginTop: 12 },
  infoBox: { marginTop: 24, padding: '14px 18px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, fontSize: 13, lineHeight: 1.7, color: '#4B5563' },
  successBox: { background: '#D1FAE5', color: '#065F46', padding: '14px 18px', borderRadius: 10, marginBottom: 20, fontSize: 13, lineHeight: 1.7 },
  error: { background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, marginTop: 16, fontSize: 13, fontWeight: 500 },
  fieldGroup: { marginBottom: 20 },
  label: { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 2, color: '#6B7280', marginBottom: 8, textTransform: 'uppercase' },
  input: { width: '100%', padding: '12px 16px', fontSize: 14, border: '1px solid #E5E7EB', borderRadius: 8, outline: 'none', color: '#1a2035', background: '#FAFBFC', boxSizing: 'border-box', transition: 'border-color 0.2s' },
  captchaBox: { background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 16px', fontFamily: 'monospace', fontSize: 18, fontWeight: 700, letterSpacing: 2, userSelect: 'none', whiteSpace: 'nowrap' },
  submitBtn: { width: '100%', padding: '14px', fontSize: 13, fontWeight: 700, letterSpacing: 2, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', textTransform: 'uppercase', marginTop: 8, transition: 'opacity 0.2s' },
  backLink: { fontSize: 13, color: '#6B7280', textDecoration: 'none' },
  footer: { fontSize: 11, color: '#9CA3AF', lineHeight: 1.5, textAlign: 'center' },
  right: { flex: '0 0 50%', position: 'relative', overflow: 'hidden' },
  heroBg: { position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'right center' },
  heroTint: { position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0.15) 70%, rgba(0,0,0,0) 100%)' },
  heroRadial: { position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(55% 60% at 30% 50%, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 100%)' },
  heroContent: { position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', padding: '60px 50px', textShadow: '0 2px 12px rgba(0,0,0,0.55)' },
  heroIcon: { width: 64, height: 64, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24, background: 'rgba(255,255,255,0.10)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.20)' },
  heroTitle: { fontSize: 40, fontWeight: 800, color: '#fff', margin: 0, lineHeight: 1.1, letterSpacing: '-0.01em' },
  heroBar: { width: 48, height: 3, borderRadius: 2, marginTop: 16, marginBottom: 16 },
  heroSub: { fontSize: 12, fontWeight: 700, letterSpacing: 4, color: 'rgba(255,255,255,0.90)', textTransform: 'uppercase', marginBottom: 24 },
  heroDesc: { fontSize: 15, color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, maxWidth: 400 },
};
